// Copyright (c) Meta Platforms, Inc. and affiliates.

/**
 * @file FieldLabel.tsx
 * @input Uses React, Icon, IconType
 * @output Exports FieldLabel component, FieldLabelProps
 * @position Core label implementation; used by Field, CheckboxInput, Switch
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Field/Field.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Field/index.ts (exports if types change)
 * - /packages/cli/templates/blocks/components/Field/ (showcase blocks)
 */

import * as stylex from '@stylexjs/stylex';
import { mergeProps } from "../utils/index.js";
import "../theme/tokens.stylex.js";
import { colorVars, fontWeightVars, spacingVars, typographyVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { Icon, renderIconSlot } from "../Icon/index.js";
import { Tooltip } from "../Tooltip/index.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Label + description group for form fields. Handles sr-only hiding,
 * disabled styling, optional/required indicators, icons, and tooltips.
 *
 * When `isLabelHidden` is true the entire group uses sr-only positioning
 * so it takes up zero layout space — no wrapper div left in flow.
 *
 * @example
 * ```
 * <FieldLabel label="Email" inputID={inputId} description="We won't share it" />
 * <FieldLabel label="Search" inputID={inputId} isLabelHidden />
 * ```
 */
export function FieldLabel({
  label,
  inputID,
  labelID,
  isGroupLabel = false,
  isLabelHidden = false,
  isDisabled = false,
  isOptional = false,
  isRequired = false,
  labelIcon,
  labelTooltip,
  description,
  descriptionID,
  ref
}) {
  const statusText = isOptional ? 'Optional' : isRequired ? 'Required' : null;

  // A group label (e.g. for a radiogroup) must not be a literal `<label>`
  // element: a `<label>` semantically names a single form control and can't be
  // associated with a group. Render it as a `<span>` instead, keeping all the
  // label styling and slots. The group references it via `aria-labelledby`.
  const LabelElement = isGroupLabel ? 'span' : 'label';
  const labelContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [labelIcon && renderIconSlot(labelIcon, {
      size: 'sm',
      color: 'inherit'
    }), label, statusText && /*#__PURE__*/_jsxs("span", {
      ...{
        className: "x1sodnla x141an7d x1ltkj2j xv1l7n4"
      },
      children: [/*#__PURE__*/_jsx("span", {
        "aria-hidden": "true",
        children: " \u2219 "
      }), statusText]
    }), labelTooltip && /*#__PURE__*/_jsx(Tooltip, {
      content: labelTooltip,
      placement: "above",
      children: /*#__PURE__*/_jsx(Icon, {
        icon: "info",
        size: "sm",
        color: "inherit"
      })
    })]
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(LabelElement, {
      ref: ref,
      id: labelID
      // `htmlFor` only applies to a real `<label>` associating with a single
      // control; a group label (span) has no `htmlFor`.
      ,
      htmlFor: isGroupLabel ? undefined : inputID,
      ...mergeProps(themeProps('field-label'), {
        0: {
          className: "x78zum5 x6s0dn4 xzye2dw x9ynric xcr08ib x1kq96og x1e4wzip xv1l7n4 x1ypdohk"
        },
        2: {
          className: "x78zum5 x6s0dn4 xzye2dw x9ynric xcr08ib x1kq96og x1e4wzip xnbbluu x1h6gzvc"
        },
        1: {
          className: "x78zum5 x6s0dn4 xzye2dw x9ynric xcr08ib x1kq96og x1e4wzip xv1l7n4 x1ypdohk xng3xce xzpqnlu xjm9jq1 xu96u03 xkdpibf xb3r6kr x1717udv x47corl x10l6tqk x13vifvy x87ps6o xuxw1ft x1i1rx1s"
        },
        3: {
          className: "x78zum5 x6s0dn4 xzye2dw x9ynric xcr08ib x1kq96og x1e4wzip xnbbluu x1h6gzvc xng3xce xzpqnlu xjm9jq1 xu96u03 xkdpibf xb3r6kr x1717udv x47corl x10l6tqk x13vifvy x87ps6o xuxw1ft x1i1rx1s"
        }
      }[!!isDisabled << 1 | !!isLabelHidden << 0]),
      children: labelContent
    }), description && /*#__PURE__*/_jsx("span", {
      id: descriptionID,
      ...{
        0: {
          className: "x9ynric x141an7d x1ltkj2j x1sodnla xv1l7n4"
        },
        1: {
          className: "x9ynric x141an7d x1ltkj2j x1sodnla xv1l7n4 xng3xce xzpqnlu xjm9jq1 xu96u03 xkdpibf xb3r6kr x1717udv x47corl x10l6tqk x13vifvy x87ps6o xuxw1ft x1i1rx1s"
        }
      }[!!isLabelHidden << 0],
      children: description
    })]
  });
}
FieldLabel.displayName = 'FieldLabel';